/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { JobFinderListItem } from '@gh/builder-shared';
import {
  ContactDeliveryMethod,
  DRAW_RULE_LENDERS_SCHEDULE_CODE,
  InvoicePhysicalType,
  InvoiceStatus,
  isLendersScheduleCode,
  JobDrawCalculateRequestDto,
  JobDrawCreateLendersScheduleRequestDto,
  JobDrawCreatePaymentScheduleRequestDto,
  JobDrawDetailsChangeOrderDto,
  JobDrawDetailsCurrentInvoiceDto,
  JobDrawDetailsDto,
  JobDrawDetailsInvoiceDto,
  JobDrawDetailsLendersScheduleItemDto,
  JobDrawDetailsPaymentScheduleItemDto,
  JobDrawDetailsStageDto,
  JobDrawGeneratedDocsResponseDto,
  JobDrawPrintRequestDto,
  JobFinanceType,
} from '@gh/builder-shared-data';
import {
  composeSetters,
  createArrayHelper,
  EMPTY_FORM_STATE_SNAPSHOT,
  EntityConfig,
  entityValidatorByEntityConfig,
  fieldStateByEntityConfig,
  fieldValidationSetter,
  fieldValidatorByEntityConfig,
  formStateSnapshot,
  FormStateSnapshot,
  getFormStateSnapshotValue,
  IS_REGION_AU_OR_NZ,
  NumberTypes,
  setEntityField,
  setEntityFields,
  setFormStateSnapshotValue,
} from '@gh/common-shared';
import {
  EMPTY_ID,
  EMPTY_PRODUCE_STATUS,
  ID,
  ProduceStatus,
  WithEntityState,
  WithProduceStatus,
  WithRecalculationNeeded,
  WithVersion,
} from '@gh/common-shared-data';
import {
  bySelectionModel,
  emptySelectionModel,
  isSelectionModelEmpty, mergeWithPrivelegedTokens,
  SelectionModel,
  selectionModelSelectAll,
} from '@gh/core-data';
import { validationError } from '@gh/core-ui';
import { FocusedCell } from '@gh/core-ui-grid';
import {
  coalesce,
  composeComparators,
  currentNormalizedDate,
  isDateValid,
  maxWith,
  toString,
  valueComparator,
} from '@gh/core-util';
import { find, get, isNil, max, maxBy, negate, omit, pick, sumBy } from 'lodash';

export type DrawDetailsMasterInfo = {
  jobId: ID;
  jobNumber: string;
  customerId: ID;
  customerName: string;
  customerEmail: string;
  customerMethod: ContactDeliveryMethod;
  siteAddress: string;
  financeCompanyAddress?: string;
  financeCompanyName?: string;
  financeType?: JobFinanceType;
};

export type DrawDetailsPaymentInfo = {
  contractDrawnAmount: number;
  variationsDrawnAmount: number;
  totalDrawnAmount: number;
  totalPaid: number;
  totalCreditNotes: number;
  totalBalance: number;
  contractAmount: number;
  contractAmountWithGst: number;
  accountingDate: Date;
  drawRuleId: ID;
  drawRuleCode: string;
  drawRuleName: string;
  drawRuleStage: number;
  targetDrawRuleStage?: number;
};

export type DrawDetailsLendersInfo = {
  drawRuleCode: string;
  contractDrawnAmount: number;
  variationsDrawnAmount: number;
  totalDrawnAmount: number;
  totalPaid: number;
  totalCreditNotes: number;
  totalBalance: number;
  contractAmount: number;
  accountingDate: Date;
  contractAmountLenders: number;
  isShowAllCostCenters: boolean;
  invoiceNumber?: number;
  invoiceSeqNumber?: number;
  costLend: number;
  costLendIncluded: number;
};

export type DrawDetailsPaymentProgressInfo = {
  invoiceNumber?: number;
  totalDue: number;
  invoicedToDate: number;

  thisDraw: number;
  thisDrawGstAmount: number;
  thisDrawAmountWithGst: number;
  correction: number;

  recommendedDraw: number;
  recommendedDrawGstAmount: number;
  recommendedDrawAmountWithGst: number;

  memo: string;
  historyAreaMemo: string;

  taxRate?: number;
  gstApplies: boolean;
};
export type DrawDetailsPaymentProgressValidationInfo = DrawDetailsPaymentProgressInfo & {
  hasSelectedChangeOrders: boolean;
};

export type DrawDetailsPaymentScheduleItem = JobDrawDetailsPaymentScheduleItemDto;
export type DrawDetailsLendersScheduleItem = WithEntityState<JobDrawDetailsLendersScheduleItemDto & {
  isTotal: boolean;
  isAllOrderCostsDrawn: boolean;
  isBalance: boolean;
}>;
export type DrawDetailsInvoice = JobDrawDetailsInvoiceDto;
export type DrawDetailsCurrentInvoice = JobDrawDetailsCurrentInvoiceDto;
export type DrawDetailsStage = JobDrawDetailsStageDto;
export type DrawDetailsChangeOrder = JobDrawDetailsChangeOrderDto;

export type DrawCalculateRequest = JobDrawCalculateRequestDto;
export type DrawCalculateResponse = {
  thisDrawAmountWithGst: number;
  recommendedAmountWithGst: number;
};
export type DrawGeneratedDocs = JobDrawGeneratedDocsResponseDto;
export type DrawPrintRequest = JobDrawPrintRequestDto;

export enum DrawDetailsPaymentTab { ChangeOrders = 'ChangeOrders', Progress = 'Progress' }

export type DrawDetails = WithProduceStatus<WithRecalculationNeeded<WithVersion<{
  job: DrawDetailsMasterInfo;
  paymentInfo: FormStateSnapshot<DrawDetailsPaymentInfo>;
  lendersInfo: FormStateSnapshot<DrawDetailsLendersInfo>;
  paymentProgressInfo: FormStateSnapshot<DrawDetailsPaymentProgressInfo>;
  stages: DrawDetailsStage[];
  paymentSchedule: DrawDetailsPaymentScheduleItem[];
  lendersSchedule: DrawDetailsLendersScheduleItem[];
  changeOrders: DrawDetailsChangeOrder[];
  invoices: DrawDetailsInvoice[];
  currentInvoiceId?: ID;
  currentInvoice?: DrawDetailsCurrentInvoice;
  selectedTab: DrawDetailsPaymentTab;
  selectedChangeOrders: SelectionModel;
  lendersScheduleFocusedCell?: FocusedCell;
  isQuoteVariationExist: boolean;
  tabs: {
    isChangeOrdersVisited: boolean;
    isProgressVisited: boolean;
  };
  isCloud: boolean;
  isPreview: boolean;
}>>>;

export const DRAW_DETAILS_INIT: DrawDetails = {
  job: <any>void 0,
  paymentInfo: EMPTY_FORM_STATE_SNAPSHOT,
  lendersInfo: EMPTY_FORM_STATE_SNAPSHOT,
  paymentProgressInfo: EMPTY_FORM_STATE_SNAPSHOT,
  stages: [],
  paymentSchedule: [],
  lendersSchedule: [],
  changeOrders: [],
  invoices: [],
  currentInvoice: void 0,
  selectedTab: DrawDetailsPaymentTab.ChangeOrders,
  selectedChangeOrders: emptySelectionModel(),
  isQuoteVariationExist: false,
  tabs: {
    isChangeOrdersVisited: false,
    isProgressVisited: false,
  },
  isPreview: false,
  isCloud: false,
  produceStatus: EMPTY_PRODUCE_STATUS,
};

export const drawDetailsForMakeByPaymentScheduleFromDto = (dto: JobDrawDetailsDto,
                                                           produceStatus: ProduceStatus,
                                                           isCloud: boolean): DrawDetails => {
  let nextDrawRuleStage = Math.min(
    coalesce(
      max(dto.invoices
        .filter((invoice) => invoice.drawRuleId === dto.drawRuleId)
        .filter(isDrawProgressInvoice)
        .map((invoice) => invoice.drawRuleStage)),
      0) + 1,
    coalesce(max(dto.paymentSchedule.map((item) => item.stageNumber)), 0));

  if (nextDrawRuleStage === 1) {
    const deletedFirstStage = dto.drawRuleStages.findIndex((el) => el.stageNumber === 1) >= 0;
    nextDrawRuleStage = deletedFirstStage ? 1 : 2;
  }

  return selectDrawRuleStage(
    {
      job: dto.jobId ? jobFromDrawDetailsDto(dto) : <any>void 0,
      paymentInfo: formStateSnapshot({
        ...pick(dto, [
          'contractDrawnAmount', 'variationsDrawnAmount', 'totalDrawnAmount', 'totalPaid', 'totalPaid', 'totalCreditNotes',
          'totalBalance', 'contractAmount', 'contractAmountWithGst', 'drawRuleId', 'drawRuleCode', 'drawRuleName',
        ]),
        accountingDate: currentNormalizedDate(),
        // define next draw rule stage to be paid
        drawRuleStage: nextDrawRuleStage,
        progressDrawRuleStage: get(
          maxWith(
            dto.invoices.filter(isDrawProgressInvoice),
            composeComparators(valueComparator('accountingDate'), valueComparator('invoiceNumber'))),
          'drawRuleStage'),
      }),
      lendersInfo: EMPTY_FORM_STATE_SNAPSHOT,
      paymentProgressInfo: setFormStateSnapshotValue(EMPTY_FORM_STATE_SNAPSHOT, (value) => ({
        ...value,
        taxRate: dto.gstApplies && dto.taxRate ? dto.taxRate : void 0,
        gstApplies: dto.gstApplies,
      })),
      selectedTab: DrawDetailsPaymentTab.Progress,
      selectedChangeOrders: emptySelectionModel(),
      stages: dto.drawRuleStages,
      paymentSchedule: dto.paymentSchedule,
      lendersSchedule: [],
      changeOrders: dto.variations,
      invoices: dto.invoices,
      version: dto.version,
      isRecalculationNeeded: false,
      isQuoteVariationExist: dto.isQuoteVariationExist,
      tabs: {
        isChangeOrdersVisited: false,
        isProgressVisited: false,
      },
      isPreview: false,
      isCloud,
      produceStatus,
    },
    nextDrawRuleStage);
};

export const drawDetailsForMakeByLendersScheduleFromDto = (dto: JobDrawDetailsDto,
                                                           produceStatus: ProduceStatus,
                                                           isCloud: boolean): DrawDetails => {
  const maxSeqNumber = get(maxBy(dto.invoices, 'seqNumber'), 'seqNumber');

  const includedSchedule = dto.lendersSchedule.filter(negate(isDrawLendersScheduleItemBalance));
  const isAllOrderCostsDrawn = sumBy(includedSchedule, 'amountDrawn') >= dto.costLendIncluded;

  const schedule = dto.lendersSchedule.map((item) => ({
    ...item,
    isTotal: false,
    isBalance: isDrawLendersScheduleItemBalance(item),
    isAllOrderCostsDrawn,
  }));

  return {
    job: dto.jobId ? jobFromDrawDetailsDto(dto) : <any>void 0,
    paymentInfo: EMPTY_FORM_STATE_SNAPSHOT,
    lendersInfo: formStateSnapshot({
      ...<DrawDetailsLendersInfo>pick(dto, [
        'contractDrawnAmount', 'variationsDrawnAmount', 'totalDrawnAmount', 'totalPaid', 'totalCreditNotes',
        'totalBalance', 'contractAmount', 'contractAmountLenders', 'costLendIncluded', 'costLend', 'drawRuleCode',
      ]),
      accountingDate: currentNormalizedDate(),
      isShowAllCostCenters: true,
      invoiceSeqNumber: maxSeqNumber,
    }),
    paymentProgressInfo: EMPTY_FORM_STATE_SNAPSHOT,
    selectedTab: DrawDetailsPaymentTab.Progress,
    selectedChangeOrders: emptySelectionModel(),
    stages: dto.drawRuleStages,
    paymentSchedule: [],
    lendersSchedule: schedule,
    changeOrders: dto.variations,
    invoices: dto.invoices,
    version: dto.version,
    isRecalculationNeeded: false,
    isQuoteVariationExist: dto.isQuoteVariationExist,
    tabs: {
      isChangeOrdersVisited: false,
      isProgressVisited: false,
    },
    isPreview: false,
    isCloud,
    produceStatus,
  };
};

export const drawDetailsForInvoiceFromDto = (dto: JobDrawDetailsDto,
                                             currentInvoiceId: ID,
                                             produceStatus: ProduceStatus,
                                             isCloud: boolean): DrawDetails =>
  isLendersScheduleCode(dto.drawRuleCode)
    ? drawDetailsForInvoiceByLendersScheduleFromDto(dto, currentInvoiceId, produceStatus, isCloud)
    : drawDetailsForInvoiceByPaymentScheduleFromDto(dto, currentInvoiceId, produceStatus, isCloud);

export const drawDetailsForInvoiceByPaymentScheduleFromDto = (dto: JobDrawDetailsDto,
                                                              currentInvoiceId: ID,
                                                              produceStatus: ProduceStatus,
                                                              isCloud: boolean): DrawDetails => {
  const currentInvoice = dto.invoice!;
  const details: DrawDetails = {
    job: dto.jobId ? pick(dto, [
      'jobId', 'jobNumber', 'customerId', 'customerName', 'customerEmail', 'customerMethod', 'siteAddress',
    ]) : <any>void 0,
    paymentInfo: formStateSnapshot({
      drawRuleId: currentInvoice.drawRuleId,
      drawRuleCode: dto.drawRuleCode,
      drawRuleName: currentInvoice.drawRuleName,
      contractDrawnAmount: currentInvoice.historyProgressClaims,
      variationsDrawnAmount: currentInvoice.historyVarsClaims,
      totalDrawnAmount: currentInvoice.historyProgressClaims + currentInvoice.historyVarsClaims,
      totalPaid: currentInvoice.historyPaid,
      totalCreditNotes: currentInvoice.historyCreditNotes,
      totalBalance: currentInvoice.historyProgressClaims + currentInvoice.historyVarsClaims
        - currentInvoice.historyPaid,
      accountingDate: currentInvoice.accountingDate,
      drawRuleStage: currentInvoice.drawRuleStage,
      contractAmount: currentInvoice.contractAmount,
      contractAmountWithGst: currentInvoice.contractAmountWithGst,
      progressDrawRuleStage: currentInvoice.drawRuleStage,
    }),
    paymentProgressInfo: EMPTY_FORM_STATE_SNAPSHOT,
    lendersInfo: EMPTY_FORM_STATE_SNAPSHOT,
    selectedTab: DrawDetailsPaymentTab.Progress,
    selectedChangeOrders: emptySelectionModel(),
    stages: [{
      id: 'draw-rule-stage',
      name: currentInvoice.drawRuleStageName,
      stageNumber: currentInvoice.drawRuleStage,
    }],
    paymentSchedule: dto.paymentSchedule,
    lendersSchedule: [],
    changeOrders: dto.variations,
    invoices: dto.invoices,
    currentInvoiceId,
    currentInvoice: dto.invoice,
    version: dto.version,
    isRecalculationNeeded: false,
    isQuoteVariationExist: dto.isQuoteVariationExist,
    tabs: {
      isChangeOrdersVisited: false,
      isProgressVisited: false,
    },
    isPreview: false,
    isCloud,
    produceStatus,
  };

  return {
    ...details,
    paymentProgressInfo: formStateSnapshot({
      invoiceNumber: currentInvoice.invoiceNumber,
      totalDue: currentInvoice.historyTotalDue,
      invoicedToDate: currentInvoice.historyInvoiced,
      thisDraw: currentInvoice.amount,
      thisDrawAmountWithGst: currentInvoice.amountWithGst,
      thisDrawGstAmount: currentInvoice.amountWithGst - currentInvoice.amount,
      correction: currentInvoice.correction,
      recommendedDraw: currentInvoice.historyRecAmount,
      recommendedDrawAmountWithGst: currentInvoice.historyRecAmount + currentInvoice.historyRecGst,
      recommendedDrawGstAmount: currentInvoice.historyRecGst,
      historyAreaMemo: currentInvoice.historyAreaMemo,
      memo: currentInvoice.memo,
      taxRate: currentInvoice.taxRate || void 0,
      gstApplies: dto.gstApplies,
    }),
  };
};

export const drawDetailsForInvoiceByLendersScheduleFromDto = (dto: JobDrawDetailsDto,
                                                              currentInvoiceId: ID,
                                                              produceStatus: ProduceStatus,
                                                              isCloud: boolean): DrawDetails => {
  const currentInvoice = dto.invoice!;
  const schedule = dto.lendersSchedule
    .filter((item) => item.thisDraw)
    .map((item) => ({
      ...item,
      isTotal: false,
      isBalance: isDrawLendersScheduleItemBalance(item),
      isAllOrderCostsDrawn: false,
    }));
  return {
    job: dto.jobId ? jobFromDrawDetailsDto(dto) : <any>void 0,
    paymentInfo: EMPTY_FORM_STATE_SNAPSHOT,
    lendersInfo: formStateSnapshot({
      drawRuleCode: dto.drawRuleCode,
      contractDrawnAmount: currentInvoice.historyProgressClaims,
      variationsDrawnAmount: currentInvoice.historyVarsClaims,
      totalDrawnAmount: currentInvoice.historyProgressClaims + currentInvoice.historyVarsClaims,
      totalPaid: currentInvoice.historyPaid,
      totalCreditNotes: currentInvoice.historyCreditNotes,
      totalBalance: currentInvoice.historyProgressClaims + currentInvoice.historyVarsClaims
        - currentInvoice.historyPaid,
      invoiceNumber: currentInvoice.invoiceNumber,
      accountingDate: currentInvoice.accountingDate,
      isShowAllCostCenters: true,
      invoiceSeqNumber: currentInvoice.seqNumber,
      contractAmount: currentInvoice.contractAmount,
      contractAmountWithGst: currentInvoice.contractAmountWithGst,
      contractAmountLenders: currentInvoice.contractAmountLenders,
      costLendIncluded: currentInvoice.lendersScheduleCostIncluded,
      costLend: currentInvoice.lendersScheduleCost,
    }),
    paymentProgressInfo: EMPTY_FORM_STATE_SNAPSHOT,
    selectedTab: DrawDetailsPaymentTab.Progress,
    selectedChangeOrders: emptySelectionModel(),
    stages: dto.drawRuleStages,
    paymentSchedule: [],
    lendersSchedule: schedule,
    changeOrders: dto.variations,
    invoices: dto.invoices,
    currentInvoiceId,
    currentInvoice: dto.invoice,
    version: dto.version,
    isRecalculationNeeded: false,
    isQuoteVariationExist: dto.isQuoteVariationExist,
    tabs: {
      isChangeOrdersVisited: false,
      isProgressVisited: false,
    },
    isPreview: false,
    isCloud,
    produceStatus,
  };
};

export const drawDetailsToCreatePaymentScheduleRequestDto = (details: DrawDetails,
                                                             historyAreaMemo: string): JobDrawCreatePaymentScheduleRequestDto => {
  const paymentInfo = getFormStateSnapshotValue(details.paymentInfo);
  const paymentProgressInfo = getFormStateSnapshotValue(details.paymentProgressInfo);

  return {
    jobId: details.job.jobId,
    contractDrawnAmount: paymentInfo.contractDrawnAmount,
    variationsDrawnAmount: paymentInfo.variationsDrawnAmount,
    totalPaid: paymentInfo.totalPaid,
    totalCreditNotes: paymentInfo.totalCreditNotes,
    historyAreaMemo,
    totalDue: paymentProgressInfo.totalDue,
    invoicedToDate: paymentProgressInfo.invoicedToDate,
    recAmount: paymentProgressInfo.recommendedDraw,
    recGst: paymentProgressInfo.recommendedDrawGstAmount,
    contractAmount: paymentInfo.contractAmount,
    contractAmountWithGst: paymentInfo.contractAmountWithGst,
    accountingDate: paymentInfo.accountingDate,
    drawRuleId: paymentInfo.drawRuleId,
    drawStageNumber: paymentInfo.drawRuleStage,
    variations: details.changeOrders.filter(bySelectionModel(details.selectedChangeOrders, 'variationId')),
    correction: paymentProgressInfo.correction,
    drawAmount: paymentProgressInfo.thisDraw,
    drawAmountWithGst: paymentProgressInfo.thisDrawAmountWithGst,
    drawRecAmountWithGst: paymentProgressInfo.recommendedDrawAmountWithGst,
    memo: paymentProgressInfo.memo,
    taxRate: paymentProgressInfo.taxRate || void 0,
    isPreview: details.isPreview,
    privilegedTokens: mergeWithPrivelegedTokens(paymentProgressInfo),
  };
};

export const drawDetailsToCreateLendersScheduleRequestDto = (details: DrawDetails): JobDrawCreateLendersScheduleRequestDto => {
  const lendersInfo = getFormStateSnapshotValue(details.lendersInfo);

  return {
    accountingDate: lendersInfo.accountingDate,
    contractAmount: lendersInfo.contractAmount,
    contractAmountLenders: lendersInfo.contractAmountLenders,
    costLend: lendersInfo.costLend,
    costLendIncluded: lendersInfo.costLendIncluded,
    contractDrawnAmount: lendersInfo.contractDrawnAmount,
    variationsDrawnAmount: lendersInfo.variationsDrawnAmount,
    totalPaid: lendersInfo.totalPaid,
    totalCreditNotes: lendersInfo.totalCreditNotes,
    historyAreaMemo: '',
    totalThisDraw: sumBy(details.lendersSchedule, 'thisDraw'),
    lendersSchedule: details.lendersSchedule
      .filter((item) => item.thisDraw)
      .map((item) =>
        <DrawDetailsLendersScheduleItem>omit(item, ['isTotal', 'isBalance', 'isAllOrderCostsDrawn'])),
    isPreview: details.isPreview,
  };
};

export const drawDetailsToCalculateRequestDto = (details: DrawDetails,
                                                 field: 'thisDraw' | 'recommendedDraw'): JobDrawCalculateRequestDto => {
  const progressInfo = getFormStateSnapshotValue(details.paymentProgressInfo);
  return {
    amount: progressInfo[field],
    gstPct: progressInfo.taxRate || 0,
    correction: progressInfo.correction,
  };
};

export const selectDrawRuleStage = (details: DrawDetails, nextDrawRuleStage: number): DrawDetails => {
  const drawRuleId = getFormStateSnapshotValue(details.paymentInfo).drawRuleId;
  const variationsToBeSelected = selectChangeOrdersForStage(details.changeOrders, drawRuleId, nextDrawRuleStage);
  const paymentProgressInfo = buildPaymentProgressInfo(details, nextDrawRuleStage);
  const selectedTab = isSelectionModelEmpty(variationsToBeSelected)
    ? DrawDetailsPaymentTab.Progress
    : DrawDetailsPaymentTab.ChangeOrders;
  return setEntityFields(details, {
    paymentProgressInfo: formStateSnapshot(paymentProgressInfo),
    selectedTab,
    selectedChangeOrders: variationsToBeSelected,
    tabs: {
      isChangeOrdersVisited: selectedTab === DrawDetailsPaymentTab.ChangeOrders,
      isProgressVisited: selectedTab === DrawDetailsPaymentTab.Progress,
    },
  });
};

export const buildPaymentProgressInfo = (details: DrawDetails,
                                         drawRuleStage: number): DrawDetailsPaymentProgressInfo => {
  const { paymentSchedule: schedule, invoices } = details;
  const lastStageNumber = getDrawLastStageNumber(details);
  const scheduleItem = find(schedule, ({ stageNumber }) => stageNumber === drawRuleStage)!;
  // if last stage is selected => take contract amount
  // otherwise => sum all amounts of stages till this one
  const totalDue = drawRuleStage === lastStageNumber
    ? getFormStateSnapshotValue(details.paymentInfo).contractAmount
    : sumBy(schedule.filter(({ stageNumber }) => stageNumber <= drawRuleStage), (item) => item.amountDue);
  const paymentProgressInfo = getFormStateSnapshotValue(details.paymentProgressInfo);
  return {
    invoiceNumber: void 0,
    totalDue,
    invoicedToDate: sumBy(invoices.filter(isDrawProgressApprovedInvoice), (invoice) => invoice.amount),
    thisDraw: scheduleItem.thisDraw,
    thisDrawAmountWithGst: scheduleItem.thisDraw,
    thisDrawGstAmount: 0,
    correction: 0,
    recommendedDraw: scheduleItem.thisDraw,
    recommendedDrawAmountWithGst: scheduleItem.thisDraw,
    recommendedDrawGstAmount: 0,
    memo: '',
    historyAreaMemo: '',
    taxRate: paymentProgressInfo.taxRate,
    gstApplies: paymentProgressInfo.gstApplies,
  };
};

export const selectChangeOrdersForStage = (changeOrders: DrawDetailsChangeOrder[],
                                           drawRuleId: ID,
                                           drawRuleStage: number) =>
  selectionModelSelectAll(
    emptySelectionModel(),
    changeOrders
    // select change orders which belong to the same draw rule id and has less or equal stage
      .filter((co) => co.drawRuleId === drawRuleId && co.drawRuleStage <= drawRuleStage)
      .map((co) => co.variationId));

export const drawDetailsMasterInfoFromFinder = (item: JobFinderListItem): DrawDetailsMasterInfo => ({
  jobId: item.id,
  jobNumber: item.jobNumber,
  customerName: item.contactName,
  siteAddress: item.siteAddress,
  customerId: EMPTY_ID,
  customerEmail: '',
  customerMethod: ContactDeliveryMethod.Mail,
});

export const isDrawProgressInvoice = (invoice: DrawDetailsInvoice) => isNil(invoice.variationId);

const INVOICE_APPROVED_TYPES: InvoicePhysicalType[] = [InvoicePhysicalType.Accrec, InvoicePhysicalType.Accreccreadit];
const INVOICE_APPROVED_STATUS: InvoiceStatus[] = [InvoiceStatus.Authorized, InvoiceStatus.Paid];
export const isDrawProgressApprovedInvoice = (invoice: DrawDetailsInvoice) =>
  isNil(invoice.variationId)
  && INVOICE_APPROVED_STATUS.includes(invoice.status)
  && INVOICE_APPROVED_TYPES.includes(invoice.invoiceType);

export const isDrawTaxRateRequired = (thisDraw: number, recommendedDraw: number, hasSelectedChangeOrders: boolean) =>
  IS_REGION_AU_OR_NZ && (thisDraw !== 0 || !(thisDraw === 0 && recommendedDraw === 0 && hasSelectedChangeOrders));

const DRAW_PAYMENT_INFO: EntityConfig<DrawDetailsPaymentInfo> = {
  accountingDate: { required: true, validator: (_1, _2, date: Date) => validateNotFutureDate(date) },
};

const DRAW_PAYMENT_PROGRESS_INFO: EntityConfig<DrawDetailsPaymentProgressValidationInfo> = {
  thisDraw: { required: true },
  taxRate: {
    required: ({ thisDraw, recommendedDraw, hasSelectedChangeOrders, gstApplies }) =>
      isDrawTaxRateRequired(thisDraw, recommendedDraw, hasSelectedChangeOrders) && gstApplies,
  },
};

export const validateDrawPaymentInfo = entityValidatorByEntityConfig(DRAW_PAYMENT_INFO);
export const validateDrawPaymentProgressInfo = entityValidatorByEntityConfig(DRAW_PAYMENT_PROGRESS_INFO);

export const isNotFutureDate = (date: Date) => date <= currentNormalizedDate();
export const validateNotFutureDate = (date: Date) =>
  isDateValid(date) && isNotFutureDate(date)
    ? void 0
    : { 'future': validationError('msg_field_invalid_format') };

export const mergeDrawDetailsAmountWithGst = (details: DrawDetails, response: DrawCalculateResponse) =>
  setEntityField(
    details,
    'paymentProgressInfo',
    setFormStateSnapshotValue(details.paymentProgressInfo, (info) => setEntityFields(info, {
      thisDrawAmountWithGst: response.thisDrawAmountWithGst,
      thisDrawGstAmount: response.thisDrawAmountWithGst - info.thisDraw,
      recommendedDrawAmountWithGst: response.recommendedAmountWithGst,
      recommendedDrawGstAmount: response.recommendedAmountWithGst - info.recommendedDraw,
    })));

export const queryDrawDetailsIsLendersSchedule = (details: DrawDetails) => {
  const lendersInfoDrawRuleCode = get(getFormStateSnapshotValue(details.lendersInfo), 'drawRuleCode');
  const paymentInfoDrawRuleCode = get(getFormStateSnapshotValue(details.paymentInfo), 'drawRuleCode');
  return lendersInfoDrawRuleCode === DRAW_RULE_LENDERS_SCHEDULE_CODE
    || paymentInfoDrawRuleCode === DRAW_RULE_LENDERS_SCHEDULE_CODE;
};

export const getDrawLendersScheduleItemId = (item: DrawDetailsLendersScheduleItem) => toString(item.code);
export const isDrawLendersScheduleItemTotal = (item: DrawDetailsLendersScheduleItem) => item.isTotal;

export const drawLendersScheduleItemArr = createArrayHelper(getDrawLendersScheduleItemId);

const DRAW_LENDERS_INFO_ENTITY_CONFIG: EntityConfig<DrawDetailsLendersInfo> = {
  accountingDate: { required: true, validator: (_1, _2, date: Date) => validateNotFutureDate(date) },
};

const DRAW_DETAILS_LENDERS_SCHEDULE_ITEM_ENTITY_CONFIG: EntityConfig<DrawDetailsLendersScheduleItem> = {
  thisDraw: {
    editable: (item) =>
      !isDrawLendersScheduleItemTotal(item)
      && (item.isBalance ? item.isAllOrderCostsDrawn : !item.isAllOrderCostsDrawn),
    type: NumberTypes.Price,
  },
};

export const validateDrawLendersInfo = entityValidatorByEntityConfig(DRAW_LENDERS_INFO_ENTITY_CONFIG);

export const validateLendersScheduleItemField =
  fieldValidatorByEntityConfig(DRAW_DETAILS_LENDERS_SCHEDULE_ITEM_ENTITY_CONFIG);
export const getLendersScheduleItemFieldState =
  fieldStateByEntityConfig(DRAW_DETAILS_LENDERS_SCHEDULE_ITEM_ENTITY_CONFIG);
export const setDrawLendersScheduleItemField =
  composeSetters(setEntityField, fieldValidationSetter(validateLendersScheduleItemField));

export const jobFromDrawDetailsDto = (dto: JobDrawDetailsDto): DrawDetailsMasterInfo =>
  pick(dto, [
    'jobId', 'jobNumber', 'customerId', 'customerName', 'customerEmail', 'customerMethod', 'siteAddress',
    'financeCompanyAddress', 'financeCompanyName', 'financeType',
  ]);

export const drawDetailsForMakeFromDto = (dto: JobDrawDetailsDto, produceStatus: ProduceStatus, isCloud: boolean): DrawDetails =>
  isLendersScheduleCode(dto.drawRuleCode)
    ? drawDetailsForMakeByLendersScheduleFromDto(dto, produceStatus, isCloud)
    : drawDetailsForMakeByPaymentScheduleFromDto(dto, produceStatus, isCloud);

export const calculateDrawLendersTotal = (items: DrawDetailsLendersScheduleItem[]): DrawDetailsLendersScheduleItem =>
  <any>items.reduce(
    (total, item) => {
      total.orderCost += item.orderCost || 0;
      total.contractCostPct += isDrawLendersScheduleItemBalance(item) ? 0 : (item.contractCostPct || 0);
      total.amountDrawn += item.amountDrawn || 0;
      total.thisDraw += item.thisDraw || 0;
      return total;
    },
    {
      code: -1,
      name: '',
      balance: 0,
      orderCost: 0,
      amountDrawn: 0,
      orderNumber: void 0,
      orderCostPct: 100,
      contractCostPct: 0,
      thisDraw: 0,
      isExcluded: void 0,
      isTotal: true,
    });

export const isDrawLendersScheduleItemBalance =
  ({ code }: DrawDetailsLendersScheduleItem | JobDrawDetailsLendersScheduleItemDto) => isNil(code);
export const isDrawLendersScheduleItemOrderCostDrawn =
  ({ orderCost, amountDrawn }: DrawDetailsLendersScheduleItem) => amountDrawn >= orderCost && amountDrawn > 0;

export const getDrawLastStageNumber = ({ stages }: DrawDetails) => get(maxBy(stages, 'stageNumber'), 'stageNumber');
